import type { Region } from '~~/core/entities/Region';

import { PropertyTypeEnum } from '~/core/entities/CRMEnums';
import { PropertyFiltersPresenter } from '~/presenters/PropertyFiltersPresenter';
import { getUrlFromStaticDNS } from '@/utils';
import { getRegionMetadata } from './utils';

const regionName = 'Vila Nova Conceição';

const metaTags = getRegionMetadata(regionName, 'São Paulo', {
	gender: 'neutral'
});

const region: Region = {
	name: regionName,
	state: 'SP',
	city: 'São Paulo',
	path: '/bairros/vila-nova-conceicao',
	image: getUrlFromStaticDNS('/images/regions/vila-nova-conceicao/banner.webp'),
	metaTags,
	info: {
		title: '',
		description: ''
	},
	questions: [
		{
			question: `Onde fica a ${regionName}?`,
			answer: `A ${regionName} está localizada na cidade de São Paulo, na zona sul, no distrito de Moema, sob a subprefeitura de Vila Mariana. O bairro faz parte de uma região nobre da cidade, fazendo divisa com os bairros Itaim Bibi, Vila Olímpia e Moema. <br/><br/>As principais avenidas de acesso ao bairro são a Avenida Hélio Pellegrino, a Avenida Indianópolis e a Avenida República do Líbano, que facilitam o acesso tanto para o centro da cidade quanto para outras regiões importantes.`
		},
		{
			question: `Como chegar na ${regionName}?`,
			answer: `A ${regionName} é bem servida por transporte privado e público. Para quem vai de carro, o bairro é facilmente acessado por importantes avenidas como a Avenida Hélio Pellegrino, Avenida Indianópolis e Avenida República do Líbano, além da proximidade com a Marginal Pinheiros, que facilita o deslocamento para outras regiões de São Paulo. <br/><br/>No transporte público, o bairro é atendido por diversas linhas de ônibus e está próximo à estação AACD-Servidor da Linha 5-Lilás do metrô. Para quem preferir, o bairro também conta com boas ciclovias, permitindo fácil acesso ao Parque Ibirapuera e outras áreas ao redor.`
		},
		{
			question: `O que fazer na ${regionName}?`,
			answer: `A ${regionName} é um bairro que combina tranquilidade e lazer, com o Parque Ibirapuera no seu coração. O parque oferece uma infinidade de atividades ao ar livre, como caminhadas, corridas e passeios de bicicleta, além de abrigar o Pavilhão Japonês e o Auditório Oscar Niemeyer, que oferecem exposições culturais e concertos. <br/><br/>Dentro do próprio parque, o Museu de Arte Moderna (MAM) traz uma rica programação de arte contemporânea. Próximo ao bairro, o MAC – Museu de Arte Contemporânea, da USP, é um importante centro cultural, com um vasto acervo e exposições de renomados artistas. Além disso, a ${regionName} conta com clubes tradicionais como o Clube Atlético Monte Líbano e o Esporte Clube Sírio, ideais para quem busca esportes e lazer.`
		},
		{
			question: `Quais são os restaurantes na ${regionName}?`,
			answer: `A ${regionName} é um verdadeiro paraíso para os amantes da gastronomia, com uma variedade de opções para todos os gostos. O Pracinha, com seu ambiente descontraído, é perfeito para quem busca um boteco clássico e um cardápio que celebra a simplicidade e o sabor. Para uma experiência mais intimista, o Enosteria se destaca com sua cozinha italiana de alta qualidade e uma carta de vinhos extensa, ideal para jantares românticos ou encontros especiais. Para quem não abre mão de um excelente café ou brunch, a Santa Etienne oferece uma padaria tradicional com uma seleção de pães e doces irresistíveis, enquanto o A Fornada é o lugar perfeito para quem aprecia sanduíches artesanais e uma variedade de pratos frescos. `
		}
	],
	hero: {
		title: regionName,
		description:
			'Sofisticação, natureza e qualidade de vida em um dos bairros mais desejados de São Paulo – tudo isso perto do seu escritório.',
		backgroundImage: getUrlFromStaticDNS(
			'/images/regions/vila-nova-conceicao/banner.webp'
		)
	},
	map: {
		title: `${regionName}: a vida ao lado do Parque Ibirapuera`,
		content: `Localizado entre o Itaim Bibi e a Vila Olímpia, a ${regionName} é o bairro perfeito para quem busca tranquilidade, mas que não quer ficar longe dos principais polos financeiros de São Paulo.<br/><br/>Sua ótima localização é só a cereja do bolo de um bairro que abriga uma ótima gastronomia, escolas, lojas e qualidade de vida. Fazendo divisa com o Parque Ibirapuera, a ${regionName} é um bairro de ruas tranquilas, um clima aconchegante e seguro. Aos fins de semana, a ${regionName} ganha vida, com as ruas tomadas de adultos e crianças que buscam, no Ibirapuera, um escape da cidade grande. Aqui, apresentamos o que torna a ${regionName} um dos bairros mais desejados pelas famílias paulistanas.`,
		image: '/images/regions/vila-nova-conceicao/map.webp',
		links: [
			{
				label: `Ver imóveis na ${regionName}`,
				url: PropertyFiltersPresenter.getPathFromFilters({
					propertyTypes: [],
					regions: [regionName],
					states: ['SP'],
					cities: ['São Paulo']
				})
			}
		]
	},
	content: [
		{
			info: {
				title: `Restaurantes na ${regionName}`,
				content: `Os amantes da boa gastronomia podem se deliciar com diversas opções de restaurantes na ${regionName}. Para quem procura uma gastronomia informal, o Pracinha é um boteco clássico do bairro, com clientes fiéis desde 1984.<br/><br/>Com um ar intimista e perfeito para um jantar à dois, o Enosteria é uma boa pedida. Por lá, o cardápio combina a tradicional comida italiana com uma carta extensa de vinhos para todos os gostos. E vale dizer que, como vários lugares no bairro, o local é pet friendly.<br/><br/>Se você não abre mão de uma boa padaria ou quer aproveitar um ótimo brunch após um café no parque, não faltam opções na ${regionName}. A tradicional Santa Etienne entrega tudo que você espera de uma padaria de São Paulo. Para quem ama um bom café em qualquer horário do dia, vale dar um pulo no A Fornada, que serve, além de ótimos sanduíches e doces, um excelente almoço.`
			},
			images: [
				{
					url: '/images/regions/vila-nova-conceicao/content/content-1.webp',
					credits: 'Dane Deaner'
				},
				{
					url: '/images/regions/vila-nova-conceicao/content/content-2.webp',
					credits: 'Tammy chan'
				}
			]
		},
		{
			info: {
				title: `Clubes da ${regionName}`,
				content: `Procura um clube para chamar de seu no bairro? A ${regionName} conta com duas opções bem próximas, ambas em Moema, bairro vizinho. <br/><br/>Ao lado do Parque das Bicicletas está localizado o tradicionalíssimo Clube Atlético Monte Líbano. Fundado em 1934 pela colônia libanesa no Brasil, o clube conta com  lazer completo, incluindo quadras poliesportivas, piscina olímpica, piscina social e uma ótima gastronomia.<br/><br/>Um pouco a frente, na Avenida Indianópolis, está a sede do Esporte Clube Sírio, fundado em 1917, por jovens imigrantes sírios e libaneses. O que começou como um time de futebol, hoje abriga diversas modalidades de esporte, dança e teatro para crianças e adultos, além das tradicionais festas especiais.`
			},
			images: [
				{
					url: '/images/regions/vila-nova-conceicao/content/content-3.webp',
					class: 'row-start-2',
					credits: 'Clube Atlético Monte Libano'
				},
				{
					url: '/images/regions/vila-nova-conceicao/content/content-4.webp',
					class: 'row-start-2',
					credits: 'Vendela Larsson'
				}
			]
		},
		{
			info: {
				title: `Parques na ${regionName}`,
				content: `Não dá para falar de ${regionName} sem falar no Parque Ibirapuera. Coração do bairro, os moradores chegam a pé tranquilamente e podem oferecer tudo que o parque tem de melhor, das exposições na Bienal à corrida em torno do lago. Acoplada no parque, a Praça Cidade de Milão virou um ponto de encontro para os pais de pet do bairro, já que conta com uma ótima área para cachorros. <br/><br/>Entre as ruas Balthazar da Veiga e Domingos Fernandes, no coração da ${regionName}, a Praça Pereira Coutinho é ótima para levar as crianças, já que conta com playground completo, além de uma academia ao ar livre.`
			},
			images: [
				{ url: '/images/regions/vila-nova-conceicao/content/content-5.webp' },
				{
					url: '/images/regions/vila-nova-conceicao/content/content-6.webp',
					class: 'col-start-2'
				}
			]
		},
		{
			info: {
				title: `Imóveis na ${regionName}`,
				content: `Se você sente falta de casas na cidade, talvez a ${regionName} seja o bairro ideal para morar. Com praças e pertinho do Parque Ibirapuera, o que não faltam são opções de condomínios de casas prontos para morar e que oferecem a segurança de um prédio em um ambiente amplo e aconchegante. É o exemplo do condomínio Casa Jardim Teviot, com moradias a partir de 676 m².<br/><br/>Com vista para o parque, o Edifício Praça de Milão é uma ótima opção para quem busca sofisticação, lazer completo e a melhor localização do bairro. Com apartamentos entre 280 m² a 418 m², o condomínio ainda conta com piscinas adulta e infantil, quadra poliesportiva, salão de festas e academia.`
			},
			images: [
				{
					url: '/images/regions/vila-nova-conceicao/content/content-7.webp',
					credits: 'Raphael Briest'
				}
			]
		}
	],
	seo: {
		title: `Quem se interessou por ${regionName} também procurou por`,
		links: [
			{
				title: 'Tipo de imóvel',
				links: [
					{
						label: 'Apartamento',
						route: PropertyFiltersPresenter.getPathFromFilters({
							propertyTypes: [PropertyTypeEnum.APARTMENT],
							states: ['sp'],
							cities: ['São Paulo'],
							regions: [regionName]
						})
					},
					{
						label: 'Cobertura',
						route: PropertyFiltersPresenter.getPathFromFilters({
							propertyTypes: [PropertyTypeEnum.PENTHOUSE],
							states: ['sp'],
							cities: ['São Paulo'],
							regions: [regionName]
						})
					},
					{
						label: 'Casa',
						route: PropertyFiltersPresenter.getPathFromFilters({
							propertyTypes: [PropertyTypeEnum.HOUSE],
							states: ['sp'],
							cities: ['São Paulo'],
							regions: [regionName]
						})
					}
				]
			},
			{
				title: ' ',
				links: [
					{
						label: 'Duplex',
						route: PropertyFiltersPresenter.getPathFromFilters({
							propertyTypes: [PropertyTypeEnum.DUPLEX],
							states: ['sp'],
							cities: ['São Paulo'],
							regions: [regionName]
						})
					},
					{
						label: 'Casa em condomínio',
						route: PropertyFiltersPresenter.getPathFromFilters({
							propertyTypes: [PropertyTypeEnum.CONDO_HOUSE],
							states: ['sp'],
							cities: ['São Paulo'],
							regions: [regionName]
						})
					},
					{
						label: 'Garden',
						route: PropertyFiltersPresenter.getPathFromFilters({
							propertyTypes: [PropertyTypeEnum.GARDEN],
							states: ['sp'],
							cities: ['São Paulo'],
							regions: [regionName]
						})
					}
				]
			},
			{
				title: 'Localização',
				links: [
					{
						label: 'Jardim Paulista, São Paulo',
						route: PropertyFiltersPresenter.getPathFromFilters({
							states: ['sp'],
							cities: ['São Paulo'],
							regions: ['Jardim Paulista']
						})
					},
					{
						label: 'Vila Nova Conceição, São Paulo',
						route: PropertyFiltersPresenter.getPathFromFilters({
							states: ['sp'],
							cities: ['São Paulo'],
							regions: ['Vila Nova Conceição']
						})
					},
					{
						label: 'Jardim América, São Paulo',
						route: PropertyFiltersPresenter.getPathFromFilters({
							states: ['sp'],
							cities: ['São Paulo'],
							regions: ['Jardim América']
						})
					}
				]
			},
			{
				title: ' ',
				links: [
					{
						label: 'Higienópolis, São Paulo',
						route: PropertyFiltersPresenter.getPathFromFilters({
							states: ['sp'],
							cities: ['São Paulo'],
							regions: ['Higienópolis']
						})
					},
					{
						label: 'Brooklin, São Paulo',
						route: PropertyFiltersPresenter.getPathFromFilters({
							states: ['sp'],
							cities: ['São Paulo'],
							regions: ['Brooklin']
						})
					},
					{
						label: 'Moema, São Paulo',
						route: PropertyFiltersPresenter.getPathFromFilters({
							states: ['sp'],
							cities: ['São Paulo'],
							regions: ['Moema']
						})
					}
				]
			}
		],
		discoveryContent: {
			heading: `Mais sobre o ${regionName}`,
			paragraphs: [
				'A Vila Nova Conceição é um dos bairros mais desejados de São Paulo, localizada entre o Itaim Bibi e a Vila Olímpia. Com ruas tranquilas e um ambiente aconchegante, o bairro oferece fácil acesso ao Parque Ibirapuera, que se torna um verdadeiro refúgio para moradores aos fins de semana.',
				'Os parques são um grande atrativo na Vila Nova Conceição. O Parque Ibirapuera, coração do bairro, oferece uma infinidade de atividades, desde corridas até exposições culturais. A Praça Cidade de Milão, dentro do parque, é um espaço popular para os amantes de pets. Embora a gastronomia também seja um destaque, com restaurantes como o Pracinha e o Enosteria, o foco no bem-estar e na natureza torna a Vila Nova Conceição um local privilegiado. Os imóveis na região incluem condomínios de casas com segurança e espaço, como o Condomínio Casa Jardim Teviot. Para quem busca sofisticação, o Edifício Praça de Milão oferece apartamentos com vista para o parque e diversas opções de lazer. Esse equilíbrio entre tranquilidade, contato com a natureza e sofisticação faz da Vila Nova Conceição o lugar ideal para quem deseja viver com qualidade e exclusividade em São Paulo.'
			]
		}
	},
	nearRegions: [
		{
			image: '/images/regions/itaim-bibi/banner.webp',
			name: 'Itaim Bibi',
			href: '/bairros/itaim-bibi'
		},
		{
			image: '/images/regions/jardim-paulista/banner.webp',
			name: 'Jardim Paulista',
			href: '/bairros/jardim-paulista'
		},
		{
			image: '/images/regions/jardim-america/banner.webp',
			name: 'Jardim América',
			href: '/bairros/jardim-america'
		},
		{
			image: '/images/regions/pinheiros/banner.webp',
			name: 'Pinheiros',
			href: '/bairros/pinheiros'
		},
		{
			image: '/images/regions/alto-de-pinheiros/banner.webp',
			name: 'Alto de Pinheiros',
			href: '/bairros/alto-de-pinheiros'
		},
		{
			image: '/images/regions/jardim-europa/banner.webp',
			name: 'Jardim Europa',
			href: '/bairros/jardim-europa'
		},
		{
			image: '/images/regions/jardim-paulistano/banner.webp',
			name: 'Jardim Paulistano',
			href: '/bairros/jardim-paulistano'
		}
	]
};

export default region;
